import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, CheckCircle, FilePdf, ShareNetwork, WhatsappLogo, Heart,
  ListDashes, Cards, CalendarBlank, Clock, Confetti, CaretRight,
  CalendarCheck, DownloadSimple, GoogleLogo, Plus, ArrowsOut, X, ArrowSquareOut,
  PencilSimple, Check, Trash, LinkSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getSchedule, setScheduleTitle, setScheduleRoundLabel, setScheduleEventLinks } from "../lib/api";
import { useAuth } from "../lib/AuthProvider";

const CARD_BG = "https://images.unsplash.com/photo-1635776062127-d379bfcba9f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGxpZ2h0JTIwZ3JhZGllbnQlMjBwdXJwbGUlMjBjeWFufGVufDB8fHx8MTc4Mzc2NDkxNHww&ixlib=rb-4.1.0&q=85";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtDate = (iso, raw) => {
  if (!iso) return raw || "—";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return raw || iso;
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};
const isChoice = (e) => e.is_choice_filling || /choice.?fill|locking/i.test(e.activity || "");
const roundLabel = (r, kind) => {
  if (r == null) return kind === "exam" ? "Exam Schedule" : "Pre-Counselling";
  if (r >= 100) return "Stray Vacancy Round";
  return `Round ${r}`;
};

function groupByRound(events, kind) {
  const map = new Map();
  events.forEach((e) => {
    const key = e.round == null ? "gen" : String(e.round);
    if (!map.has(key)) map.set(key, { key, round: e.round, label: roundLabel(e.round, kind), events: [] });
    map.get(key).events.push(e);
  });
  // chronological — earliest event start decides group order (falls back to round number)
  return [...map.values()].sort((a, b) => {
    const sa = roundRange(a.events).start || "9999-99-99";
    const sb = roundRange(b.events).start || "9999-99-99";
    if (sa !== sb) return sa.localeCompare(sb);
    return (a.round ?? -1) - (b.round ?? -1);
  });
}

// ─── calendar / date-range helpers ───────────────────────────────────────
const pad = (n) => String(n).padStart(2, "0");
const toICS = (iso) => iso.replace(/-/g, "");
const addDay = (iso) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
};
function roundRange(events) {
  const starts = events.map((e) => e.start).filter(Boolean).sort();
  const ends = events.map((e) => e.end || e.start).filter(Boolean).sort();
  return { start: starts[0] || null, end: ends[ends.length - 1] || null };
}
function rangeLabel(events) {
  const { start, end } = roundRange(events);
  if (!start) return null;
  if (!end || end === start) return fmtDate(start);
  const sy = start.slice(0, 4), ey = end.slice(0, 4);
  const s = new Date(start + "T00:00:00"), e = new Date(end + "T00:00:00");
  const left = sy === ey ? `${s.getDate()} ${MONTHS[s.getMonth()]}` : fmtDate(start);
  return `${left} – ${fmtDate(end)}`;
}
function gcalLink(ev) {
  if (!ev.start) return null;
  const text = encodeURIComponent(ev.activity || "Counselling event");
  const details = encodeURIComponent([ev.time ? `Time: ${ev.time}` : "", ev.notes || ""].filter(Boolean).join("\n"));
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${toICS(ev.start)}/${addDay(ev.end || ev.start)}&details=${details}`;
}
function buildICS(sched) {
  const lines = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Unicators//Schedule//EN", "CALSCALE:GREGORIAN"];
  (sched.events || []).forEach((e, i) => {
    if (!e.start) return;
    lines.push(
      "BEGIN:VEVENT",
      `UID:neetcounsel-${sched.body_slug}-${sched.slug}-${i}@neetcounsel`,
      `DTSTART;VALUE=DATE:${toICS(e.start)}`,
      `DTEND;VALUE=DATE:${addDay(e.end || e.start)}`,
      `SUMMARY:${(e.activity || "Event").replace(/[\r\n]+/g, " ")}`,
      `DESCRIPTION:${[e.time ? `Time: ${e.time}` : "", e.notes || "", sched.title || ""].filter(Boolean).join(" | ").replace(/[\r\n]+/g, " ")}`,
      "END:VEVENT");
  });
  lines.push("END:VCALENDAR");
  return lines.join("\r\n");
}
function downloadICS(sched) {
  const blob = new Blob([buildICS(sched)], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${sched.body_slug}-${sched.slug}.ics`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
function monthsWithEvents(events) {
  const map = new Map();
  events.forEach((e) => {
    if (!e.start) return;
    const key = e.start.slice(0, 7);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(e);
  });
  return [...map.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([key, evs]) => ({ key, evs }));
}

export default function SchedulePage() {
  const { bodySlug, slug } = useParams();
  const { isAdmin } = useAuth();
  const [sched, setSched] = useState(null);
  const [err, setErr] = useState("");
  const [view, setView] = useState("timeline");
  const [liked, setLiked] = useState(false);

  const reload = () =>
    getSchedule(bodySlug, slug)
      .then((r) => { r.data?.error ? setErr(r.data.error) : setSched(r.data); })
      .catch(() => setErr("Could not load this schedule."));

  useEffect(() => {
    let alive = true;
    getSchedule(bodySlug, slug)
      .then((r) => { if (!alive) return; r.data?.error ? setErr(r.data.error) : setSched(r.data); })
      .catch(() => alive && setErr("Could not load this schedule."));
    return () => { alive = false; };
  }, [bodySlug, slug]);

  const groups = useMemo(() => {
    if (!sched) return [];
    const gs = groupByRound(sched.events || [], sched.kind);
    const rl = sched.round_labels || {};
    gs.forEach((g) => { if (rl[g.key]) g.label = rl[g.key]; });
    return gs;
  }, [sched]);
  const nextUp = useMemo(() => {
    if (!sched) return null;
    const today = new Date().toISOString().slice(0, 10);
    return (sched.events || []).find((e) => e.start && e.start >= today) || null;
  }, [sched]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = sched ? `${sched.title || "Counselling Schedule"} — Unicators` : "Unicators Schedule";
  const doShare = async () => {
    if (navigator.share) { try { await navigator.share({ title: shareText, url: shareUrl }); return; } catch (e) { /* */ } }
    navigator.clipboard?.writeText(shareUrl);
  };
  const waShare = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`, "_blank");

  if (err) return (
    <div className="min-h-screen grid place-items-center bg-zinc-50" data-testid="schedule-error">
      <div className="text-center">
        <p className="text-zinc-500 mb-3">{err}</p>
        <Link to="/counselling" className="text-[#6043EF] font-bold text-sm hover:underline">← Back to Counselling</Link>
      </div>
    </div>
  );
  if (!sched) return <div className="min-h-screen grid place-items-center bg-zinc-50 text-zinc-400" data-testid="schedule-loading">Loading schedule…</div>;

  const streamLabel = (sched.stream || "ug").toUpperCase();

  return (
    <div className="min-h-screen bg-gradient-to-b from-fuchsia-50/60 via-white to-sky-50/40">
      {/* Header */}
      <div className="sticky top-0 z-30 backdrop-blur-xl backdrop-saturate-150 bg-white/70 border-b border-white/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3">
          <Link to="/counselling" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#6043EF] hover:underline" data-testid="schedule-back-link">
            <ArrowLeft size={12} weight="bold" /> Counselling
          </Link>
          <div className="flex items-start justify-between gap-3 mt-1.5">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#6043EF] text-white text-[10px] font-black uppercase tracking-wider">{sched.short} · {streamLabel} {sched.year}</span>
                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600" data-testid="schedule-verified">
                  <CheckCircle size={14} weight="fill" /> Official
                </span>
              </div>
              <h1 className="text-lg sm:text-2xl font-black tracking-tight text-[#09090B] mt-1 line-clamp-2 font-heading flex items-start gap-2" data-testid="schedule-header-title">
                <span className="min-w-0">{sched.title || `${sched.body} Counselling Schedule ${sched.year}`}</span>
                {isAdmin && (
                  <InlineTitleEditor
                    current={sched.title || ""}
                    onSave={async (v) => { await setScheduleTitle(bodySlug, slug, v); await reload(); toast.success("Title updated"); }}
                  />
                )}
              </h1>
              <p className="text-[11px] text-zinc-400 mt-0.5">{sched.body}{sched.published_at ? ` · published ${fmtDate(sched.published_at.slice(0,10))}` : ""}</p>
              {(sched.links || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="schedule-useful-links">
                  {sched.links.map((l, i) => (
                    <a key={i} href={l.url} target="_blank" rel="noreferrer" data-testid={`useful-link-${i}`}
                      className={`inline-flex items-center gap-1 h-6 px-2.5 rounded-full text-[10px] font-bold border transition-colors ${l.kind === "apply" ? "bg-red-50 border-red-300 text-red-700 hover:bg-red-100" : "bg-white border-zinc-200 text-zinc-600 hover:border-[#6043EF] hover:text-[#6043EF]"}`}>
                      <ArrowSquareOut size={11} weight="bold" /> {l.kind === "apply" ? "Apply / Register" : l.kind === "bulletin" ? "Information Bulletin" : l.kind === "contact" ? "Contact / Helpdesk" : (l.label || "Link").slice(0, 32)}
                    </a>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {sched.apply_link && (
                <a href={sched.apply_link} target="_blank" rel="noreferrer" data-testid="schedule-apply-btn"
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-red-600 text-white text-[11px] font-black uppercase tracking-wider hover:bg-red-700 shadow shadow-red-500/30 transition-colors">
                  Apply <ArrowSquareOut size={13} weight="bold" />
                </a>
              )}
              {sched.url && (
                <a href={sched.url} target="_blank" rel="noreferrer" data-testid="schedule-pdf-link"
                  className="inline-flex items-center gap-1 h-8 px-2.5 rounded-full bg-white border border-zinc-200 text-[11px] font-bold text-rose-600 hover:bg-rose-50">
                  <FilePdf size={14} weight="fill" /> PDF
                </a>
              )}
              <button onClick={waShare} data-testid="share-whatsapp-btn"
                className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 transition-colors">
                <WhatsappLogo size={16} weight="fill" />
              </button>
              <button onClick={doShare} data-testid="share-btn"
                className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-[#6043EF] text-white hover:bg-[#4f36c9] transition-colors">
                <ShareNetwork size={16} weight="fill" />
              </button>
            </div>
          </div>

          {/* View toggle */}
          <div className="mt-3 inline-flex items-center gap-1 p-1 rounded-full bg-zinc-100 relative" role="tablist">
            {[
              { k: "timeline", label: "Timeline", Icon: ListDashes },
              { k: "cards", label: "Story Cards", Icon: Cards },
              { k: "calendar", label: "Calendar", Icon: CalendarCheck },
            ].map(({ k, label, Icon }) => (
              <button key={k} role="tab" aria-selected={view === k} onClick={() => setView(k)}
                data-testid={`view-toggle-${k}`}
                className={`relative z-10 inline-flex items-center gap-1.5 h-8 px-4 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${view === k ? "text-white" : "text-zinc-500 hover:text-zinc-700"}`}>
                {view === k && <motion.span layoutId="view-pill" className="absolute inset-0 rounded-full bg-[#6043EF] shadow-[0_0_20px_rgba(96,67,239,0.5)]" style={{ zIndex: -1 }} transition={{ type: "spring", stiffness: 400, damping: 32 }} />}
                <Icon size={14} weight={view === k ? "fill" : "regular"} /> {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <AnimatePresence mode="wait">
          {view === "timeline" ? (
            <motion.div key="timeline" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
              <TimelineView groups={groups} nextUp={nextUp} isAdmin={isAdmin} bodySlug={bodySlug} slug={slug} reload={reload} />
            </motion.div>
          ) : view === "cards" ? (
            <motion.div key="cards" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }} transition={{ duration: 0.3 }}>
              <StoryCardsView groups={groups} sched={sched} nextUp={nextUp} liked={liked} setLiked={setLiked} waShare={waShare} doShare={doShare} />
            </motion.div>
          ) : (
            <motion.div key="calendar" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
              <CalendarView sched={sched} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function TimelineView({ groups, nextUp, isAdmin, bodySlug, slug, reload }) {
  return (
    <div className="relative">
      {groups.map((g) => (
        <div key={g.key} className="mb-8">
          <div className="sticky top-[132px] z-20 -mx-1 mb-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#09090B] text-white text-xs font-black uppercase tracking-wider shadow-lg" data-testid={`round-group-${g.key}`}>
            {g.label} <span className="text-white/50 font-semibold normal-case">· {g.events.length} steps</span>
            {isAdmin && (
              <InlineRoundLabelEditor
                current={g.label}
                onSave={async (v) => { await setScheduleRoundLabel(bodySlug, slug, g.key, v); await reload(); toast.success("Round title updated"); }}
              />
            )}
          </div>
          <div className="relative pl-6 border-l-2 border-[#6043EF]/20 ml-2 space-y-3">
            {g.events.map((e, i) => {
              const cf = isChoice(e);
              const isNext = nextUp && e.start === nextUp.start && e.activity === nextUp.activity;
              return (
                <div key={i} className="relative" data-testid={`timeline-event-${g.key}-${i}`}>
                  <span className={`absolute -left-[31px] top-3 h-3.5 w-3.5 rounded-full border-2 border-white ${isNext ? "bg-[#6043EF]" : cf ? "bg-[#00E5FF]" : "bg-zinc-300"}`}>
                    {isNext && <span className="absolute inset-0 rounded-full bg-[#6043EF] animate-ping" />}
                  </span>
                  <div className={`rounded-2xl border p-3.5 transition-all duration-300 hover:-translate-y-0.5 ${cf ? "border-cyan-300 bg-cyan-50/60 shadow-[0_0_18px_rgba(0,229,255,0.18)]" : "bg-white border-zinc-200 shadow-sm hover:shadow-md"}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-bold text-sm text-[#09090B] leading-snug">{e.activity}</p>
                        {e.notes && <p className="text-[11px] text-zinc-400 mt-0.5">{e.notes}</p>}
                      </div>
                      {cf && <span className="shrink-0 text-[9px] font-black uppercase tracking-wider text-cyan-700 bg-cyan-100 px-2 py-0.5 rounded-full">Choice filling</span>}
                      {isNext && <span className="shrink-0 text-[9px] font-black uppercase tracking-wider text-white bg-[#6043EF] px-2 py-0.5 rounded-full animate-pulse">Next up</span>}
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-[11px] font-semibold text-zinc-500 font-mono tabular-nums">
                      <span className="inline-flex items-center gap-1"><CalendarBlank size={13} weight="bold" className="text-[#6043EF]" /> {fmtDate(e.start, e.date)}{e.end && e.end !== e.start ? ` → ${fmtDate(e.end)}` : ""}</span>
                      {e.time && <span className="inline-flex items-center gap-1"><Clock size={13} weight="bold" className="text-[#6043EF]" /> {e.time}</span>}
                    </div>
                    {(e.link || (e.extra_links || []).length > 0) && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {e.link && (
                          <a href={e.link} target="_blank" rel="noreferrer" data-testid={`event-apply-${g.key}-${i}`}
                            className="inline-flex items-center gap-1 h-7 px-3 rounded-full bg-red-600 text-white text-[10px] font-black uppercase tracking-wider hover:bg-red-700 shadow shadow-red-500/30 transition-colors">
                            {e.link_label || "Apply"} <ArrowSquareOut size={11} weight="bold" />
                          </a>
                        )}
                        {(e.extra_links || []).map((l, li) => (
                          <a key={li} href={l.url} target="_blank" rel="noreferrer" data-testid={`event-link-${g.key}-${i}-${li}`}
                            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-white border border-zinc-200 text-[10px] font-bold text-zinc-600 hover:border-[#6043EF] hover:text-[#6043EF] transition-colors">
                            <LinkSimple size={11} weight="bold" /> {(l.label || "Link").slice(0, 32)}
                          </a>
                        ))}
                      </div>
                    )}
                    {isAdmin && (
                      <EventLinksEditor
                        links={e.extra_links || []}
                        eventKey={e.event_key}
                        testid={`${g.key}-${i}`}
                        onSave={async (links) => { await setScheduleEventLinks(bodySlug, slug, e.event_key, links); await reload(); toast.success("Links saved"); }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Admin inline editors ────────────────────────────────────────────────
function InlineTitleEditor({ current, onSave }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(current);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(current); }, [current]);
  const save = async () => { setBusy(true); try { await onSave(val.trim()); setOpen(false); } catch { toast.error("Save failed"); } finally { setBusy(false); } };
  if (!open) return (
    <button onClick={() => setOpen(true)} data-testid="edit-title-btn"
      className="shrink-0 mt-1 h-6 w-6 grid place-items-center rounded-full bg-zinc-100 text-zinc-500 hover:bg-[#6043EF] hover:text-white transition-colors" title="Edit title">
      <PencilSimple size={13} weight="bold" />
    </button>
  );
  return (
    <span className="inline-flex items-center gap-1 w-full">
      <input autoFocus value={val} onChange={(e) => setVal(e.target.value)} data-testid="edit-title-input"
        placeholder="Custom schedule title (blank = auto)"
        className="flex-1 min-w-0 h-8 px-2 rounded-lg border border-[#6043EF]/40 text-sm font-semibold text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#6043EF]/30" />
      <button onClick={save} disabled={busy} data-testid="save-title-btn" className="h-8 w-8 grid place-items-center rounded-lg bg-[#6043EF] text-white hover:bg-[#4f36c9] disabled:opacity-50"><Check size={15} weight="bold" /></button>
      <button onClick={() => { setOpen(false); setVal(current); }} className="h-8 w-8 grid place-items-center rounded-lg bg-zinc-100 text-zinc-500 hover:bg-zinc-200"><X size={15} weight="bold" /></button>
    </span>
  );
}

function InlineRoundLabelEditor({ current, onSave }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(current);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(current); }, [current]);
  const save = async () => { setBusy(true); try { await onSave(val.trim()); setOpen(false); } catch { toast.error("Save failed"); } finally { setBusy(false); } };
  if (!open) return (
    <button onClick={(ev) => { ev.stopPropagation(); setOpen(true); }} data-testid="edit-round-btn"
      className="ml-1 h-5 w-5 grid place-items-center rounded-full bg-white/15 hover:bg-white/30 transition-colors" title="Edit round title">
      <PencilSimple size={11} weight="bold" />
    </button>
  );
  return (
    <span className="inline-flex items-center gap-1 ml-1 normal-case tracking-normal" onClick={(e) => e.stopPropagation()}>
      <input autoFocus value={val} onChange={(e) => setVal(e.target.value)} data-testid="edit-round-input"
        placeholder="Round title (blank = auto)"
        className="h-6 px-2 rounded text-[11px] font-semibold text-[#09090B] bg-white focus:outline-none w-44" />
      <button onClick={save} disabled={busy} data-testid="save-round-btn" className="h-6 w-6 grid place-items-center rounded bg-[#6043EF] text-white disabled:opacity-50"><Check size={12} weight="bold" /></button>
      <button onClick={() => { setOpen(false); setVal(current); }} className="h-6 w-6 grid place-items-center rounded bg-white/20 text-white"><X size={12} weight="bold" /></button>
    </span>
  );
}

function EventLinksEditor({ links, eventKey, testid, onSave }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(links);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setRows(links.length ? links : [{ label: "", url: "" }]); }, [open]); // eslint-disable-line
  const upd = (i, k, v) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const add = () => setRows((r) => (r.length >= 5 ? r : [...r, { label: "", url: "" }]));
  const del = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const save = async () => {
    setBusy(true);
    try { await onSave(rows.filter((r) => (r.url || "").trim()).slice(0, 5)); setOpen(false); }
    catch { toast.error("Save failed"); } finally { setBusy(false); }
  };
  if (!open) return (
    <button onClick={() => setOpen(true)} data-testid={`add-event-link-btn-${testid}`}
      className="mt-2 inline-flex items-center gap-1 h-6 px-2 rounded-full border border-dashed border-[#6043EF]/40 text-[10px] font-bold uppercase tracking-wider text-[#6043EF] hover:bg-violet-50 transition-colors">
      <Plus size={11} weight="bold" /> {links.length ? `Edit links (${links.length})` : "Add link"}
    </button>
  );
  return (
    <div className="mt-2 rounded-xl border border-[#6043EF]/30 bg-violet-50/40 p-2.5 space-y-1.5" data-testid={`event-links-editor-${testid}`}>
      {rows.map((row, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <input value={row.label} onChange={(e) => upd(i, "label", e.target.value)} placeholder="Link name" data-testid={`event-link-name-${testid}-${i}`}
            className="h-7 w-28 px-2 rounded border border-zinc-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-[#6043EF]/40" />
          <input value={row.url} onChange={(e) => upd(i, "url", e.target.value)} placeholder="https://…" data-testid={`event-link-url-${testid}-${i}`}
            className="h-7 flex-1 min-w-0 px-2 rounded border border-zinc-200 text-[11px] font-mono focus:outline-none focus:ring-1 focus:ring-[#6043EF]/40" />
          <button onClick={() => del(i)} className="h-7 w-7 grid place-items-center rounded bg-white text-rose-500 hover:bg-rose-50 border border-zinc-200"><Trash size={12} weight="bold" /></button>
        </div>
      ))}
      <div className="flex items-center justify-between pt-0.5">
        <button onClick={add} disabled={rows.length >= 5} data-testid={`event-link-add-row-${testid}`}
          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#6043EF] disabled:opacity-40 hover:underline">
          <Plus size={11} weight="bold" /> Add another {rows.length >= 5 && "(max 5)"}
        </button>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setOpen(false)} className="h-7 px-2.5 rounded text-[10px] font-bold uppercase text-zinc-500 bg-white border border-zinc-200 hover:bg-zinc-50">Cancel</button>
          <button onClick={save} disabled={busy} data-testid={`save-event-links-${testid}`}
            className="h-7 px-3 rounded text-[10px] font-black uppercase tracking-wider bg-[#6043EF] text-white hover:bg-[#4f36c9] disabled:opacity-50">{busy ? "Saving…" : "Save links"}</button>
        </div>
      </div>
    </div>
  );
}

function EventBullets({ events }) {
  return (
    <ul className="space-y-3">
      {events.map((e, i) => {
        const c = isChoice(e);
        return (
          <li key={i} className="flex gap-3">
            <span className={`mt-1 h-2.5 w-2.5 rounded-full shrink-0 ${c ? "bg-[#00E5FF] shadow-[0_0_10px_#00E5FF]" : "bg-white/50"}`} />
            <div className="min-w-0">
              <p className={`text-sm font-semibold leading-snug ${c ? "text-cyan-200" : "text-white"}`}>{e.activity}</p>
              <p className="text-[11px] text-white/70 font-mono tabular-nums">{fmtDate(e.start, e.date)}{e.end && e.end !== e.start ? ` → ${fmtDate(e.end)}` : ""}{e.time ? ` · ${e.time}` : ""}</p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function CardHead({ g, sched }) {
  const cf = g.events.filter(isChoice).length;
  const range = rangeLabel(g.events);
  return (
    <div className="relative z-10">
      <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-cyan-300">{sched.short} · {(sched.stream || "").toUpperCase()} {sched.year}</p>
      <h2 className="text-3xl md:text-4xl font-black tracking-tighter mt-1 font-heading text-white drop-shadow-lg">{g.label}</h2>
      <p className="text-white/70 text-[11px] font-bold uppercase tracking-[0.2em] mt-0.5">Timetable</p>
      {range && (
        <span className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-full bg-cyan-400/15 border border-cyan-300/40 text-cyan-100 text-sm font-bold font-mono tabular-nums shadow-[0_0_18px_rgba(0,229,255,0.25)]" data-testid={`card-range-${g.key}`}>
          <CalendarBlank size={15} weight="fill" className="text-cyan-300" /> {range}
        </span>
      )}
      <p className="text-white/60 text-xs mt-2">{g.events.length} steps{cf ? ` · ${cf} choice-filling window${cf > 1 ? "s" : ""}` : ""}</p>
    </div>
  );
}

function StoryCardsView({ groups, sched, nextUp, liked, setLiked, waShare, doShare }) {
  const [open, setOpen] = useState(null);
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="story-grid">
        {groups.map((g, gi) => (
          <button key={g.key} type="button" data-testid={`story-card-${g.key}`} onClick={() => setOpen(gi)}
            className="group relative text-left rounded-3xl overflow-hidden border border-white/10 shadow-xl min-h-[440px] flex flex-col p-6 text-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-300 transition-transform hover:-translate-y-1">
            <img src={CARD_BG} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-b from-indigo-950/85 via-[#2A1B6D]/90 to-black/92" />
            <span className="absolute top-4 right-4 z-10 h-8 w-8 grid place-items-center rounded-full bg-black/40 backdrop-blur-xl border border-white/15 text-white/80 group-hover:scale-110 transition-transform"><ArrowsOut size={15} weight="bold" /></span>
            <CardHead g={g} sched={sched} />
            <div className="relative z-10 mt-4 flex-1 rounded-2xl bg-black/40 backdrop-blur-xl border border-white/10 p-4 overflow-hidden max-h-[210px]">
              <EventBullets events={g.events} />
              <div className="absolute bottom-0 inset-x-0 h-12 bg-gradient-to-t from-black/70 to-transparent rounded-b-2xl" />
            </div>
            <span className="relative z-10 mt-4 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-cyan-200">Tap to view full story <CaretRight size={12} weight="bold" /></span>
          </button>
        ))}
      </div>
      {open !== null && (
        <StoryFullscreen groups={groups} sched={sched} nextUp={nextUp} startIndex={open} onClose={() => setOpen(null)}
          liked={liked} setLiked={setLiked} waShare={waShare} doShare={doShare} />
      )}
    </>
  );
}

function StoryFullscreen({ groups, sched, nextUp, startIndex, onClose, liked, setLiked, waShare, doShare }) {
  const scroller = useRef(null);
  const [active, setActive] = useState(startIndex);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const el = scroller.current;
    if (el) el.scrollTop = startIndex * el.clientHeight;
    return () => { document.body.style.overflow = ""; };
  }, [startIndex]);

  useEffect(() => {
    const onKey = (e) => {
      const el = scroller.current;
      if (!el) return;
      if (e.key === "Escape") { onClose(); return; }
      if (["ArrowDown", "ArrowRight", "PageDown", " "].includes(e.key)) {
        e.preventDefault();
        el.scrollTo({ top: Math.min(groups.length - 1, active + 1) * el.clientHeight, behavior: "smooth" });
      }
      if (["ArrowUp", "ArrowLeft", "PageUp"].includes(e.key)) {
        e.preventDefault();
        el.scrollTo({ top: Math.max(0, active - 1) * el.clientHeight, behavior: "smooth" });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, groups.length, onClose]);

  const onScroll = () => { const el = scroller.current; if (el) setActive(Math.round(el.scrollTop / el.clientHeight)); };
  const goTo = (i) => { const el = scroller.current; if (el) el.scrollTo({ top: i * el.clientHeight, behavior: "smooth" }); };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-[60] bg-black" data-testid="story-fullscreen">
      <div className="fixed top-3 left-1/2 -translate-x-1/2 z-[70] w-full max-w-md px-4 flex gap-1">
        {groups.map((_, i) => (
          <button key={i} onClick={() => goTo(i)} className="h-1 flex-1 rounded-full bg-white/25 overflow-hidden">
            <div className={`h-full bg-white transition-all duration-300 ${i <= active ? "w-full" : "w-0"}`} />
          </button>
        ))}
      </div>
      <button onClick={onClose} data-testid="story-close"
        className="fixed top-6 right-4 z-[70] h-10 w-10 grid place-items-center rounded-full bg-white/10 backdrop-blur-xl border border-white/20 text-white hover:bg-white/20 transition-colors">
        <X size={18} weight="bold" />
      </button>
      <div className="hidden md:block fixed bottom-4 left-1/2 -translate-x-1/2 z-[70] text-white/50 text-[11px] font-semibold tracking-wide">use ↑ ↓ arrow keys · Esc to close</div>

      <div ref={scroller} onScroll={onScroll} className="h-full w-full snap-y snap-mandatory overflow-y-auto scrollbar-hide">
        {groups.map((g, gi) => (
          <div key={g.key} data-testid={`story-full-${g.key}`} className="h-full w-full snap-center snap-always relative flex items-stretch justify-center">
            <div className="relative w-full max-w-md mx-auto flex flex-col p-6 pt-12 pb-16 text-white justify-between overflow-hidden">
              <img src={CARD_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-b from-indigo-950/80 via-[#2A1B6D]/85 to-black/92" />
              <CardHead g={g} sched={sched} />
              <div className="relative z-10 rounded-2xl bg-black/40 backdrop-blur-xl border border-white/10 p-4 max-h-[46%] overflow-y-auto scrollbar-hide">
                <EventBullets events={g.events} />
              </div>
              <div className="absolute right-3 bottom-28 z-20 flex flex-col gap-3">
                <button onClick={() => setLiked((v) => !v)} data-testid="like-btn" className="h-11 w-11 grid place-items-center rounded-full bg-black/40 backdrop-blur-xl border border-white/15 hover:scale-110 transition-transform">
                  <Heart size={20} weight={liked ? "fill" : "regular"} className={liked ? "text-rose-500" : "text-white"} />
                </button>
                <button onClick={waShare} className="h-11 w-11 grid place-items-center rounded-full bg-black/40 backdrop-blur-xl border border-white/15 hover:scale-110 transition-transform">
                  <WhatsappLogo size={20} weight="fill" className="text-emerald-400" />
                </button>
                <button onClick={doShare} className="h-11 w-11 grid place-items-center rounded-full bg-black/40 backdrop-blur-xl border border-white/15 hover:scale-110 transition-transform">
                  <ShareNetwork size={20} weight="fill" className="text-white" />
                </button>
              </div>
              <div className="relative z-10 flex items-center justify-between">
                {nextUp ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-[11px] font-bold" data-testid="next-up-badge">
                    <Confetti size={14} weight="fill" className="text-cyan-300" /> Next: {nextUp.activity.slice(0, 24)} · <span className="font-mono">{fmtDate(nextUp.start)}</span>
                  </span>
                ) : <span />}
                {gi < groups.length - 1 && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-white/60 animate-pulse">swipe up <CaretRight size={12} weight="bold" className="rotate-90" /></span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function CalendarView({ sched }) {
  const months = monthsWithEvents(sched.events || []);
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-5 rounded-2xl bg-white border border-zinc-200 p-4 shadow-sm">
        <CalendarCheck size={20} weight="fill" className="text-[#6043EF]" />
        <div className="mr-auto">
          <p className="text-sm font-bold text-[#09090B]">Add this schedule to your calendar</p>
          <p className="text-[11px] text-zinc-400">Imports every event into Google, Apple &amp; Outlook calendars with reminders.</p>
        </div>
        <button onClick={() => downloadICS(sched)} data-testid="download-ics-btn"
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#6043EF] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#4f36c9] transition-colors">
          <DownloadSimple size={15} weight="bold" /> Download .ics
        </button>
      </div>

      {months.map((m) => <MonthBlock key={m.key} month={m} />)}
      {!months.length && <div className="text-center text-zinc-400 py-10 text-sm">No dated events to place on a calendar.</div>}
    </div>
  );
}

function MonthBlock({ month }) {
  const [y, mo] = month.key.split("-").map(Number);
  const monthName = `${["January","February","March","April","May","June","July","August","September","October","November","December"][mo - 1]} ${y}`;
  const startDow = new Date(y, mo - 1, 1).getDay();
  const daysInMonth = new Date(y, mo, 0).getDate();
  const todayIso = new Date().toISOString().slice(0, 10);

  const weeksCount = Math.ceil((startDow + daysInMonth) / 7);
  const weeks = [];
  for (let w = 0; w < weeksCount; w++) {
    const row = [];
    for (let c = 0; c < 7; c++) {
      const day = w * 7 + c - startDow + 1;
      row.push(day >= 1 && day <= daysInMonth ? { day, iso: `${y}-${pad(mo)}-${pad(day)}` } : null);
    }
    weeks.push(row);
  }
  const events = (month.evs || []).filter((e) => e.start);
  const colOf = (iso) => new Date(iso + "T00:00:00").getDay() + 1;

  const weekBars = (week) => {
    const days = week.filter(Boolean);
    if (!days.length) return [];
    const wStart = days[0].iso, wEnd = days[days.length - 1].iso;
    const bars = events.map((e) => {
      const s = e.start, en = e.end || e.start;
      if (en < wStart || s > wEnd) return null;
      const cs = s < wStart ? wStart : s, ce = en > wEnd ? wEnd : en;
      const startCol = colOf(cs);
      return { e, startCol, span: colOf(ce) - startCol + 1, contFromPrev: s < wStart, contToNext: en > wEnd };
    }).filter(Boolean).sort((a, b) => a.startCol - b.startCol || b.span - a.span);
    const laneEnds = [];
    bars.forEach((b) => {
      let lane = laneEnds.findIndex((end) => end < b.startCol);
      if (lane === -1) lane = laneEnds.length;
      laneEnds[lane] = b.startCol + b.span - 1;
      b.lane = lane;
    });
    return bars;
  };

  return (
    <div className="mb-6 rounded-2xl bg-white border border-zinc-200 p-4 sm:p-5 shadow-sm" data-testid={`calendar-month-${month.key}`}>
      <h3 className="text-lg font-black tracking-tight text-[#09090B] mb-3 font-heading">{monthName}</h3>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map((w) => <div key={w} className="text-center text-[10px] font-black uppercase text-zinc-400 py-1">{w}</div>)}
      </div>
      {weeks.map((week, wi) => {
        const bars = weekBars(week);
        const laneCount = bars.reduce((m, b) => Math.max(m, b.lane + 1), 0);
        return (
          <div key={wi} className="mb-1">
            <div className="grid grid-cols-7 gap-1">
              {week.map((cell, ci) => {
                const isToday = cell && cell.iso === todayIso;
                const has = cell && events.some((e) => (e.start <= cell.iso) && ((e.end || e.start) >= cell.iso));
                return (
                  <div key={ci} className={`h-8 rounded-md text-right px-1.5 pt-0.5 border ${cell ? (has ? "border-zinc-200 bg-zinc-50/50" : "border-zinc-100") : "border-transparent"} ${isToday ? "ring-2 ring-[#6043EF]" : ""}`}>
                    {cell && <span className={`text-[11px] font-bold font-mono ${has ? "text-[#09090B]" : "text-zinc-300"}`}>{cell.day}</span>}
                  </div>
                );
              })}
            </div>
            {bars.length > 0 && (
              <div className="grid grid-cols-7 gap-x-1 gap-y-0.5 mt-0.5" style={{ gridAutoRows: "22px" }}>
                {bars.map((b, bi) => {
                  const cf = isChoice(b.e);
                  return (
                    <div key={bi} title={`${b.e.activity} · ${rangeLabel([b.e])}${b.e.time ? ` · ${b.e.time}` : ""}`}
                      data-testid={`calendar-bar-${month.key}-${bi}`}
                      style={{ gridColumn: `${b.startCol} / span ${b.span}`, gridRow: b.lane + 1 }}
                      className={`flex items-center gap-1 rounded-md px-1.5 text-[10px] font-bold leading-none overflow-hidden ${cf ? "bg-[#00E5FF]/25 text-cyan-700 border border-cyan-300 shadow-[0_0_10px_rgba(0,229,255,0.25)]" : "bg-[#6043EF]/15 text-[#4f36c9] border border-[#6043EF]/30"} ${b.contFromPrev ? "rounded-l-none border-l-0" : ""} ${b.contToNext ? "rounded-r-none border-r-0" : ""}`}>
                      {b.contFromPrev && <CaretRight size={9} weight="bold" className="rotate-180 shrink-0" />}
                      <span className="truncate">{b.e.activity}</span>
                      {b.contToNext && <CaretRight size={9} weight="bold" className="shrink-0 ml-auto" />}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      <div className="space-y-1.5 mt-3">
        {month.evs.map((e, i) => {
          const cf = isChoice(e);
          const link = gcalLink(e);
          return (
            <div key={i} className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs ${cf ? "bg-cyan-50 border border-cyan-200" : "bg-zinc-50"}`} data-testid={`calendar-event-${month.key}-${i}`}>
              <span className="font-mono tabular-nums font-bold text-zinc-500 shrink-0 w-36 sm:w-44">{rangeLabel([e]) || fmtDate(e.start)}</span>
              <span className={`font-semibold flex-1 min-w-0 truncate ${cf ? "text-cyan-700" : "text-zinc-700"}`}>{e.activity}{e.time ? <span className="text-zinc-400 font-normal"> · {e.time}</span> : null}</span>
              {link && (
                <a href={link} target="_blank" rel="noreferrer" data-testid={`gcal-event-${month.key}-${i}`}
                  className="inline-flex items-center gap-1 h-7 px-2 rounded-full bg-white border border-zinc-200 text-[10px] font-bold text-[#6043EF] hover:bg-violet-50 shrink-0">
                  <Plus size={11} weight="bold" /> <GoogleLogo size={12} weight="bold" />
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
